import React, { useEffect, useMemo, useState } from 'react';
import Text from './Text';
import Markdown from './Markdown';
import Image from './Image';
import Blurb from './Blurb';
import Capacity from './Capacity';
import Container from './Container';
import Content from './Content';
import Horizontal from './Horizontal';
import IconFeatures from './IconFeatures';
import Link from './Link';
import Links from './Links';
import { CMSFindYourFit, CMSHeader, CMSIconFeatures, CMSText } from './types';
import ProductSlider from './ProductSlider';
import Slides from './Slides';
import Testimonials from './Testimonials';
import LifestyleHeader from './LifestyleHeader';
import FindYourFit from './FindYourFit';
import HeaderLink from './HeaderLink';
import { useVariationId } from '@hooks/useVariationId';

type ElementProps = {
  elementType: string;
  attributes: any;
  children: ElementProps[];
};

export const withVariation = (ElementImpl) => (props: ElementProps) => {
  const ElementTest = (props: ElementProps) => {
    const { testId, variationId } = props.attributes;

    const isVariation = useVariationId(testId, variationId);

    const show = isVariation;

    if (show) {
      return <ElementImpl {...props} />;
    } else {
      return null;
    }
  };

  if (props.attributes.testId) {
    return <ElementTest {...props} />;
  } else {
    return <ElementImpl {...props} />;
  }
};

const ElementImpl = (props: ElementProps) => {
  const { elementType, ...restProps } = props;

  // console.log('Element', elementType, props);
  switch (elementType) {
    // "primitive"
    case 'text':
      return <Text component={props as CMSText} />;

    case 'markdown':
      return <Markdown {...props} />;

    case 'image':
      return <Image {...props} />;

    // "complex"
    case 'blurb':
      return <Blurb {...props} />;
    case 'content':
      return <Content {...props} />;
    case 'capacity':
      return <Capacity {...props} />;
    case 'container':
      return <Container {...props} />;
    case 'horizontal':
      return <Horizontal {...props} />;
    case 'link':
      return <Link {...props} />;
    case 'links':
      return <Links {...props} />;

    case 'iconFeatures':
      return <IconFeatures component={props as any} />;

    case 'productSlider':
      return <ProductSlider {...props} />;

    case 'slides':
      return props.attributes.role === 'testimonials' ? <Testimonials {...props} /> : <Slides {...props} />;

    case 'header':
      return <LifestyleHeader component={props as CMSHeader} />;

    case 'findYourFit':
      return <FindYourFit component={props as CMSFindYourFit} />;

    case 'headerLink':
      return <HeaderLink {...props} />;

    case 'pdpConfig':
      return null;

    default:
      // console.error('unsupported elementType', elementType);
      const isDev = window.DD_ENV === 'dev';
      return isDev ? (
        <div className='tw-border-1 tw-m-2 tw-bg-pink-200 tw-p-2'>
          Not Implemented: <b>{elementType}</b>
        </div>
      ) : null;
  }
};

const Element = withVariation(ElementImpl);

export default Element;
